import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { ShieldCheck, Printer, Download, ArrowLeft } from "lucide-react";
import api, { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import Logo from "@/components/Logo";
import { toast } from "sonner";

export default function AuditCertificate() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [doc, setDoc] = useState(null);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get(`/documents/${id}`);
        setDoc(data);
      } catch (e) {
        toast.error(formatApiError(e));
      }
    })();
  }, [id]);

  if (!doc) return <div className="p-10 text-sm text-slate-500">Loading…</div>;

  const print = () => window.print();

  return (
    <div className="bg-slate-50 dark:bg-slate-950 min-h-screen" data-testid="certificate-page">
      <style>{`
        @media print {
          .no-print { display: none !important; }
          body { background: white !important; }
        }
      `}</style>

      <div className="no-print sticky top-0 bg-white dark:bg-slate-900 border-b border-slate-200 dark:border-slate-800 px-6 py-3 flex items-center justify-between z-10">
        <Button variant="ghost" size="sm" onClick={() => navigate(`/app/documents/${id}`)}>
          <ArrowLeft className="h-4 w-4 mr-1" /> Back
        </Button>
        <div className="flex items-center gap-2">
          <Button variant="outline" onClick={print} data-testid="cert-print">
            <Printer className="h-4 w-4 mr-1" /> Print
          </Button>
          <Button
            className="bg-emerald-600 hover:bg-emerald-700 text-white"
            onClick={print}
            data-testid="cert-download"
          >
            <Download className="h-4 w-4 mr-1" /> Save as PDF
          </Button>
        </div>
      </div>

      <div className="max-w-3xl mx-auto p-8 sm:p-12 my-8 bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-xl shadow-sm">
        {/* Header */}
        <div className="flex items-center justify-between border-b border-slate-200 dark:border-slate-800 pb-6">
          <Logo className="h-8" />
          <div className="text-right">
            <div className="text-xs uppercase tracking-[0.2em] text-emerald-700 dark:text-emerald-400 font-semibold">
              Certificate of completion
            </div>
            <div className="text-[10px] font-mono text-slate-400 mt-0.5">
              ID · {doc.id.slice(0, 16)}
            </div>
          </div>
        </div>

        <div className="mt-8">
          <h1 className="text-2xl font-bold tracking-tight">{doc.title}</h1>
          <div className="mt-1 text-xs text-slate-500 font-mono">
            File · {doc.file_name}
          </div>
        </div>

        {/* Status */}
        <div className="mt-6 grid grid-cols-2 gap-x-8 gap-y-3 text-sm">
          <div>
            <div className="text-[10px] uppercase tracking-wider text-slate-500">Status</div>
            <div className="mt-0.5 capitalize font-semibold">
              {doc.status.replace(/_/g, " ")}
            </div>
          </div>
          <div>
            <div className="text-[10px] uppercase tracking-wider text-slate-500">Created</div>
            <div className="mt-0.5 font-mono">
              {new Date(doc.created_at).toLocaleString()}
            </div>
          </div>
          {doc.sent_at && (
            <div>
              <div className="text-[10px] uppercase tracking-wider text-slate-500">Sent</div>
              <div className="mt-0.5 font-mono">
                {new Date(doc.sent_at).toLocaleString()}
              </div>
            </div>
          )}
          {doc.completed_at && (
            <div>
              <div className="text-[10px] uppercase tracking-wider text-slate-500">Completed</div>
              <div className="mt-0.5 font-mono text-emerald-700 dark:text-emerald-400">
                {new Date(doc.completed_at).toLocaleString()}
              </div>
            </div>
          )}
          <div>
            <div className="text-[10px] uppercase tracking-wider text-slate-500">Signing mode</div>
            <div className="mt-0.5 capitalize">{doc.signing_mode}</div>
          </div>
          <div>
            <div className="text-[10px] uppercase tracking-wider text-slate-500">Version</div>
            <div className="mt-0.5">{doc.version_number || 1}</div>
          </div>
        </div>

        {/* Document hash */}
        <div className="mt-8 rounded-lg bg-slate-50 dark:bg-slate-800/40 border border-slate-200 dark:border-slate-700 p-4">
          <div className="flex items-center gap-2">
            <ShieldCheck className="h-4 w-4 text-emerald-600" />
            <div className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold">
              Document integrity
            </div>
          </div>
          <div className="mt-2 text-xs font-mono text-slate-700 dark:text-slate-300 break-all">
            SHA-256 · {doc.hash || "n/a"}
          </div>
        </div>

        {/* Recipients */}
        <div className="mt-8">
          <h2 className="text-sm font-semibold uppercase tracking-wider text-slate-500">
            Recipients
          </h2>
          <div className="mt-3 divide-y divide-slate-100 dark:divide-slate-800">
            {doc.recipients.map((r) => (
              <div key={r.id} className="py-3 flex items-start justify-between gap-4 text-sm">
                <div>
                  <div className="font-medium">{r.name}</div>
                  <div className="text-xs text-slate-500">{r.email}</div>
                  <div className="text-[10px] uppercase tracking-wider text-slate-400 mt-0.5">
                    {r.role} · order {r.order}
                  </div>
                </div>
                <div className="text-right text-xs">
                  <div className="capitalize font-semibold">{r.status}</div>
                  {r.signed_at && (
                    <div className="font-mono text-slate-500 mt-0.5">
                      {new Date(r.signed_at).toLocaleString()}
                    </div>
                  )}
                  {r.last_ip && (
                    <div className="text-slate-400 font-mono">IP {r.last_ip}</div>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Audit timeline */}
        <div className="mt-8">
          <h2 className="text-sm font-semibold uppercase tracking-wider text-slate-500">
            Audit trail
          </h2>
          <div className="mt-3 space-y-2.5">
            {doc.audit.map((ev) => (
              <div
                key={ev.id}
                className="grid grid-cols-[160px_1fr] gap-3 text-xs font-mono items-start"
              >
                <span className="text-slate-500 whitespace-nowrap">
                  {new Date(ev.at).toLocaleString()}
                </span>
                <span>
                  <span className="text-emerald-700 dark:text-emerald-400">{ev.event}</span>
                  {ev.actor_name && (
                    <span className="text-slate-600 dark:text-slate-300"> · {ev.actor_name}</span>
                  )}
                  {ev.ip && <span className="text-slate-500"> · {ev.ip}</span>}
                </span>
              </div>
            ))}
          </div>
        </div>

        <div className="mt-10 pt-6 border-t border-slate-200 dark:border-slate-800 text-xs text-slate-500 leading-relaxed">
          This certificate is generated by Attestra for{" "}
          <span className="font-semibold">{doc.created_by_name}</span>. It records all
          actions taken on the document with timestamps, IP addresses, and a tamper-evident
          SHA-256 hash of the original file. Treat this document as legally binding evidence
          of completion.
        </div>
      </div>
    </div>
  );
}
